import { json } from "@remix-run/cloudflare";
import {
	Link,
	type LinkProps,
	type MetaFunction,
	useLoaderData,
} from "@remix-run/react";
import { format } from "date-fns";
import type { HTMLProps } from "react";
import { Layout } from "../components/Layout";
import {
	type SitemapCategory,
	createPageHierarchy,
	getSitemapPages,
} from "../lib/sitemapUtils";

export const meta: MetaFunction = () => {
	return [{ title: "Sitemap" }];
};

export async function loader() {
	return json({ pages: await getSitemapPages() });
}

export default function Sitemap() {
	const { pages } = useLoaderData<typeof loader>();
	const pageHierarchy = createPageHierarchy(pages);

	return (
		<Layout>
			<main>
				{/* TODO: Break down `TravelHeader` and reuse it here as `PageHeader`, or similar */}
				<header className="relative mt-20 mb-10">
					<h1 className="font-semibold text-3xl">Sitemap</h1>
				</header>
				<SitemapListing level={0} pages={pageHierarchy} />
			</main>
		</Layout>
	);
}

/**
 * Props to customise specific levels of the sitemap.
 *
 * This is getting complex, and it might be better to just make a custom
 * component for each level.
 */
const sitemapLevels: {
	HeadingTag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
	headingProps?: HTMLProps<HTMLHeadingElement>;
	headingLinkProps?: Omit<LinkProps, "to">;
	listProps?: HTMLProps<HTMLUListElement>;
}[] = [
	{
		HeadingTag: "h2",
		headingProps: {
			className: "sticky top-0",
		},
		headingLinkProps: {
			className: "text-lg py-5",
		},
	},
	{
		HeadingTag: "h3",
		listProps: {
			className: "py-10 pl-10 grid lg:grid-cols-4 gap-16",
		},
		headingProps: {
			className: "border-b w-full pb-2 mb-2",
		},
	},
	{ HeadingTag: "h3" },
	{ HeadingTag: "h4" },
	{ HeadingTag: "h5" },
	{ HeadingTag: "h6" },
];

const SitemapListing = (props: { level: number; pages: SitemapCategory }) => {
	const pagesList = Object.values(props.pages);
	if (pagesList.length === 0) {
		return null;
	}

	const level = sitemapLevels[props.level];
	if (!level) {
		throw new Error(`No sitemap level for ${props.level}`);
	}

	const { HeadingTag, listProps, headingProps, headingLinkProps } = level;

	return (
		<ul {...listProps}>
			{pagesList.map((page) => (
				<li key={page.pathname} style={{ paddingLeft: props.level * 20 }}>
					<HeadingTag {...headingProps}>
						<Link
							{...headingLinkProps}
							to={page.pathname}
							reloadDocument={page.isExternalToRemix}
							className={`${
								headingLinkProps?.className ?? ""
							} flex w-full justify-between bg-gradient-to-t from-transparent to-white`}
						>
							<div>{page.sitemapTitle ?? page.title}</div>
							{page.date && (
								// TODO: Spacing. And style this page fully
								<time dateTime={page.date}>
									{format(new Date(page.date), "MMM yyyy")}
								</time>
							)}
						</Link>
					</HeadingTag>
					<SitemapListing level={props.level + 1} pages={page.children} />
				</li>
			))}
		</ul>
	);
};
